import React, { useState, useEffect, useRef } from 'react';
import './SecretsModal.css';
import { modalInputClass } from './modalInputClass';
import { useModalSelectionGuard, preventModalOverlaySelection } from '../hooks/useModalSelectionGuard';

const AutofillModal = ({
  isOpen,
  onClose,
  onSubmit,
  broker,
  defaultSum,
  defaultPercentDo,
  defaultPercentExp,
  defaultSpread
}) => {
  useModalSelectionGuard(isOpen);

  const [sum, setSum] = useState('');
  const [percentDo, setPercentDo] = useState('');
  const [percentExp, setPercentExp] = useState('');
  const [spread, setSpread] = useState('');
  const [error, setError] = useState(null);
  const wasOpenRef = useRef(false);

  useEffect(() => {
    if (isOpen) {
      if (!wasOpenRef.current) {
        const rawSum = defaultSum != null && defaultSum !== '' ? String(defaultSum).replace(/[^\d]/g, '') : '';
        setSum(rawSum);
        setPercentDo(defaultPercentDo != null && defaultPercentDo !== undefined && !isNaN(defaultPercentDo) ? Number(defaultPercentDo).toFixed(2) : '');
        setPercentExp(defaultPercentExp != null && defaultPercentExp !== undefined && !isNaN(defaultPercentExp) ? Number(defaultPercentExp).toFixed(2) : '');
        setSpread(defaultSpread != null && defaultSpread !== undefined && !isNaN(defaultSpread) ? Number(defaultSpread).toFixed(2) : '');
      }
      wasOpenRef.current = true;
      setError(null);
    } else {
      wasOpenRef.current = false;
    }
  }, [isOpen, defaultSum, defaultPercentDo, defaultPercentExp, defaultSpread]);

  const formatSumWithSpaces = (val) => {
    const digits = String(val || '').replace(/[^\d]/g, '');
    return digits.replace(/\B(?=(\d{3})+(?!\d))/g, ' ');
  };

  const handleSumChange = (e) => {
    const value = e.target.value.replace(/[^\d]/g, '');
    setSum(value);
    if (error) setError(null);
  };

  const limitToTwoDecimals = (value) => {
    const normalized = value.replace(',', '.');
    if (normalized === '' || normalized === '.') return true;
    const parts = normalized.split('.');
    return parts.length <= 1 || parts[1].length <= 2;
  };

  const handlePercentDoChange = (e) => {
    const value = e.target.value;
    if (!/^[\d.,]*$/.test(value)) return;
    if (!limitToTwoDecimals(value)) return;
    setPercentDo(value);
    if (error) setError(null);
  };

  const handlePercentExpChange = (e) => {
    const value = e.target.value;
    if (!/^[\d.,]*$/.test(value)) return;
    if (!limitToTwoDecimals(value)) return;
    setPercentExp(value);
    if (error) setError(null);
  };

  const handleSpreadChange = (e) => {
    const value = e.target.value;
    if (!/^-?[\d.,]*$/.test(value)) return;
    if (!limitToTwoDecimals(value)) return;
    setSpread(value);
    if (error) setError(null);
  };

  const handleCancel = () => {
    setSum('');
    setPercentDo('');
    setPercentExp('');
    setSpread('');
    setError(null);
    onClose();
  };

  useEffect(() => {
    if (!isOpen) return undefined;

    const handleKeyDown = (e) => {
      if (e.key === 'Escape') {
        handleCancel();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, handleCancel]);

  const defaultSumValue =
    defaultSum != null && defaultSum !== '' ? String(defaultSum).replace(/[^\d]/g, '') : '';
  const defaultPercentDoValue =
    defaultPercentDo != null && defaultPercentDo !== undefined && !isNaN(defaultPercentDo)
      ? Number(defaultPercentDo).toFixed(2)
      : '';
  const defaultPercentExpValue =
    defaultPercentExp != null && defaultPercentExp !== undefined && !isNaN(defaultPercentExp)
      ? Number(defaultPercentExp).toFixed(2)
      : '';
  const defaultSpreadValue =
    defaultSpread != null && defaultSpread !== undefined && !isNaN(defaultSpread)
      ? Number(defaultSpread).toFixed(2)
      : '';

  const handleOk = () => {
    const sumValue = parseInt(sum.replace(/\s/g, ''), 10) || 0;
    const percentDoValue = percentDo.trim() ? parseFloat(percentDo.replace(',', '.')) : null;
    const percentExpValue = percentExp.trim() ? parseFloat(percentExp.replace(',', '.')) : null;
    const spreadValue = spread.trim() ? parseFloat(spread.replace(',', '.')) : null;

    if (sumValue <= 0) {
      setError('Введите корректную сумму');
      return;
    }

    if (percentDo.trim() && (isNaN(percentDoValue))) {
      setError('%% к ДО должно быть числом');
      return;
    }

    if (percentExp.trim() && (isNaN(percentExpValue))) {
      setError('%% к эксп. должно быть числом');
      return;
    }

    if (spread.trim() && (isNaN(spreadValue))) {
      setError('Спред % должен быть числом');
      return;
    }

    const round2 = (v) => (v != null ? Math.round(v * 100) / 100 : null);
    onSubmit({
      sum: sumValue,
      percentDo: round2(percentDoValue),
      percentExp: round2(percentExpValue),
      spread: round2(spreadValue)
    });
  };

  if (!isOpen) return null;

  const brokerLabel = broker === 'bcs' ? 'БКС' : broker === 'finam' ? 'Финам' : broker;

  return (
    <div className="secrets-modal-overlay" onSelectStart={preventModalOverlaySelection}>
      <div className="secrets-modal" onClick={e => e.stopPropagation()}>
        <div className="secrets-modal-header">
          <h2>Автонабор</h2>
          <button type="button" className="secrets-close-btn" onClick={handleCancel} aria-label="Закрыть">×</button>
        </div>

        <div className="secrets-form">
          <div className="form-group">
            <label>Брокер</label>
            <input
              type="text"
              value={brokerLabel}
              readOnly
              className={modalInputClass({ readonly: true })}
            />
          </div>

          <div className="form-group">
            <label htmlFor="autofillSum">Сумма</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="autofillSum"
                value={formatSumWithSpaces(sum)}
                onChange={handleSumChange}
                placeholder="Введите сумму"
                className={modalInputClass({ withActions: true, error: Boolean(error) })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setSum(defaultSumValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить сумму по умолчанию"
                  disabled={sum === defaultSumValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSum('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить сумму"
                  disabled={!sum}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="autofillSpread">Спред % (и выше)</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="autofillSpread"
                value={spread}
                onChange={handleSpreadChange}
                placeholder="%"
                className={modalInputClass({ withActions: true, error: Boolean(error) })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setSpread(defaultSpreadValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить спред по умолчанию"
                  disabled={spread === defaultSpreadValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setSpread('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить спред"
                  disabled={!spread}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="autofillPercentDo">%% к ДО (и выше)</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="autofillPercentDo"
                value={percentDo}
                onChange={handlePercentDoChange}
                placeholder="%"
                className={modalInputClass({ withActions: true, error: Boolean(error) })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setPercentDo(defaultPercentDoValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить процент к ДО по умолчанию"
                  disabled={percentDo === defaultPercentDoValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPercentDo('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить процент к ДО"
                  disabled={!percentDo}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="autofillPercentExp">%% к эксп. (и выше)</label>
            <div className="secrets-input-with-clear">
              <input
                type="text"
                id="autofillPercentExp"
                value={percentExp}
                onChange={handlePercentExpChange}
                placeholder="%"
                className={modalInputClass({ withActions: true, error: Boolean(error) })}
              />
              <div className="secrets-input-actions">
                <button
                  type="button"
                  onClick={() => {
                    setPercentExp(defaultPercentExpValue);
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Восстановить процент к экспозиции по умолчанию"
                  disabled={percentExp === defaultPercentExpValue}
                >
                  ↺
                </button>
                <button
                  type="button"
                  onClick={() => {
                    setPercentExp('');
                    if (error) setError(null);
                  }}
                  className="secrets-input-action-btn"
                  aria-label="Очистить процент к экспозиции"
                  disabled={!percentExp}
                >
                  ×
                </button>
              </div>
            </div>
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          <div className="secrets-modal-buttons">
            <button type="button" className="secrets-cancel-btn" onClick={handleCancel}>
              Отмена
            </button>
            <button type="button" className="secrets-save-btn" onClick={handleOk}>
              Создать
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AutofillModal;
